import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import {
  TuiError,
  TuiExpand,
  TuiIcon,
  TuiButton,
  TuiHint,
  TuiInput,
  TuiDialogService,
} from '@taiga-ui/core';
import { TuiBadge, TuiSkeleton } from '@taiga-ui/kit';
import {
  AsyncPipe,
  NgClass,
  NgOptimizedImage,
  NgTemplateOutlet,
} from '@angular/common';
import {
  FileExtensionPipe,
  FilePreviewUrlPipe,
  RequestReleaseTimeParserPipe,
} from '@core/pipes/';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { IconButtonComponent } from '@common/components';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, finalize } from 'rxjs';
import {
  FileDownloadService,
  IsLoadingService,
  XAlertService,
} from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import type { RequestModel } from '@core/models';
import { FilePreviewComponent } from '@core/components';
import { TextCopierTranslations } from '@core/types';
import { WorkflowsTranslations } from '@core/types';
import type {
  Counterparty,
  RequestComment,
  RequestDevice,
  RequestFile,
  TextCopierTranslationsPayload,
  WorkflowsTranslationsPayload,
} from '@core/types';
import { counterpartyIconResolver, isImageByFilename } from '@core/helpers';
import type { Nullable } from '@core/helpers';
import type { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import { ClipboardService } from 'ngx-clipboard';
import { environment } from '@environment/environment';
import { ImagePreviewComponent } from '@core/components/image-preview/image-preview.component';
import {
  RequestFormCommentsComponent,
  WorkflowDebugComponent,
  WorkflowSubmissionsInfoComponent,
} from '@features/requests';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';
import { GoodsBlockComponent } from '../goods-block';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TuiSheetDialogService } from '@taiga-ui/addon-mobile';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { RequestWorkflowEventsDialogComponent } from '@workflows/request-workflow-events-dialog/request-workflow-events-dialog.component';
import { RequestInstallerAssignmentDialogComponent } from '../request-installer-assignment-dialog/request-installer-assignment-dialog.component';
import { RequestService } from '@core/services/request/request.service';
import { RequestFormReleaseCityComponent } from '@features/requests/request-form/components/request-form-release-city/request-form-release-city.component';
import { FeatureFlagsService } from '@core/services/feature-flags.service';

@Component({
  selector: 'x-request-form-summary-mobile',
  imports: [
    TuiSkeleton,
    AsyncPipe,
    TuiIcon,
    RequestReleaseTimeParserPipe,
    TuiHint,
    NgClass,
    TranslatePipe,
    IconButtonComponent,
    NgTemplateOutlet,
    RouterLink,
    TuiButton,
    TuiBadge,
    TuiInput,
    ReactiveFormsModule,
    TuiError,
    TuiExpand,
    NgOptimizedImage,
    ImagePreviewComponent,
    RequestFormCommentsComponent,
    GoodsBlockComponent,
    FileExtensionPipe,
    FilePreviewComponent,
    FilePreviewUrlPipe,
    RequestFormReleaseCityComponent,
  ],
  templateUrl: './request-form-summary-mobile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormSummaryMobileComponent implements OnInit {
  private translates: TextCopierTranslationsPayload = {
    [TextCopierTranslations.Copied]: 'Скопировано',
    [TextCopierTranslations.NotFoundToCopy]: 'Нечего копировать',
  };

  $request = input.required<RequestModel>({ alias: 'request' });
  $isInitialLoad = input.required<boolean>({ alias: 'isInitialLoad' });

  $formatDateWithLocale = input.required<
    (stringDate?: string | null) => string
  >({
    alias: 'formatDateWithLocale',
  });

  $getCheckList = input.required<() => void>({ alias: 'getCheckList' });
  $shareRequestLink = input.required<() => void>({ alias: 'shareRequestLink' });
  $ePTS = input<Nullable<RequestFile>>(null, { alias: 'ePTS' });
  $openYaMaps = input.required<() => void>({ alias: 'openYaMaps' });

  $devicesInstallQuality = input.required<
    Nullable<
      Record<RequestDevice['deviceHash'], DeviceInstallQualityCheckStatus>
    >
  >({ alias: 'devicesInstallQuality' });

  $requestComments = input.required<Nullable<RequestComment[]>>({
    alias: 'requestComments',
  });

  $requestDevices = input.required<Nullable<RequestDevice[]>>({
    alias: 'requestDevices',
  });

  $files = input.required<Nullable<RequestFile[]>>({
    alias: 'files',
  });

  $mainFileGroups = input.required<FileGroup[]>({
    alias: 'mainFileGroups',
  });

  $moreFileGroupsWithFiles = input.required<FileGroup[]>({
    alias: 'moreFileGroupsWithFiles',
  });

  $moreFileCatsWithFilesCount = input.required<number>({
    alias: 'moreFileCatsWithFilesCount',
  });
  $workflowUrl = input.required<string>({
    alias: 'workflowUrl',
  });
  $workflowUrlButtonText = input.required<string>({
    alias: 'workflowUrlButtonText',
  });
  $workflowDataExists = input.required<boolean>({
    alias: 'workflowDataExists',
  });

  $showPreviewImage = output<[RequestFile, FileGroup, index: number]>();

  public readonly permissionService = inject(OutkeepPermissionService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly xAlertService = inject(XAlertService);
  private readonly sheetDialogService$ = inject(TuiSheetDialogService);
  private readonly requestService = inject(RequestService);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly dialogService$ = inject(TuiDialogService);

  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);

  public expandedMoreFileCats = false;
  public readonly $isOpeningEpts = signal(false);

  private readonly $hasManageRequestWorkflowsPermission = toSignal(
    this.permissionService.canManageRequestWorkflows$,
    { initialValue: false },
  );

  private readonly $hasViewRequestWorkflowsPermission = toSignal(
    this.permissionService.canViewRequestWorkflows$,
    { initialValue: false },
  );
  protected readonly $canManageRequestWorkflows = computed(
    () =>
      this.featureFlags.workflowsEnabled &&
      this.$hasManageRequestWorkflowsPermission(),
  );
  protected readonly $canViewRequestWorkflowsPermission = computed(
    () =>
      this.featureFlags.workflowsEnabled &&
      this.$hasViewRequestWorkflowsPermission(),
  );

  ngOnInit(): void {
    this.outkeepTranslateService
      .translate([
        ...Object.values(TextCopierTranslations),
        WorkflowsTranslations.StartWorkFlowExecution,
        WorkflowsTranslations.ContinueWorkFlowExecution,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (
          translates: TextCopierTranslationsPayload &
            Partial<WorkflowsTranslationsPayload>,
        ) => (this.translates = translates),
      );
  }

  @pure
  public requestIconResolver(cpiD: Counterparty['id']) {
    return counterpartyIconResolver(cpiD);
  }

  public copyToClipboard(text: string): void {
    this.clipboardService.copy(text);
    if (!text) {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.NotFoundToCopy],
        {
          appearance: 'warning',
          autoClose: 2000,
        },
      );

      return;
    } else {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.Copied],
        {
          appearance: 'positive',
          autoClose: 2000,
        },
      );
    }
  }

  public openLastCatFile(group: FileGroup) {
    if (!group.filesExists) {
      return;
    }
    this.openFile(group.files.at(-1)!);
  }

  @pure
  public getFileUrl(file: RequestFile) {
    return environment.staticUrl + file.path;
  }

  public openEpts(): void {
    const file = this.$ePTS();

    if (!file) {
      return;
    }

    this.openFile(file, true);
  }

  private openFile(file: RequestFile, trackEptsLoading = false): void {
    if (isImageByFilename(file.filename)) {
      const previewData = this.getPreviewData(file);

      if (previewData) {
        this.$showPreviewImage.emit(previewData);

        return;
      }
    }

    if (file.filename.toLowerCase().endsWith('.pdf')) {
      if (trackEptsLoading) {
        if (this.$isOpeningEpts()) {
          return;
        }

        this.$isOpeningEpts.set(true);
        this.fileDownloadService
          .openPdfByPath$(file.path, file.filename)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => this.$isOpeningEpts.set(false)),
          )
          .subscribe();

        return;
      }

      this.fileDownloadService.openPdfByPath(file.path, file.filename);

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file.path,
      file.filename,
      false,
      this.renderer,
    );
  }

  private getPreviewData(
    file: RequestFile,
  ): [RequestFile, FileGroup, index: number] | null {
    const group = this.$mainFileGroups().find(currentGroup =>
      currentGroup.files.some(groupFile => groupFile.path === file.path),
    );

    if (!group) {
      return null;
    }

    const fileIndex = group.files.findIndex(
      groupFile => groupFile.path === file.path,
    );

    if (fileIndex < 0) {
      return null;
    }

    return [file, group, fileIndex];
  }

  // TODO TEMPORARY METHOD FOR OPENING WORKFLOWS SUBMISSIONS INFO MODAL
  public openWorkflowsDebuggingSheet(): void {
    this.sheetDialogService$
      .open(new PolymorpheusComponent(WorkflowDebugComponent), {
        closable: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  // TODO TEMPORARY METHOD FOR OPENING WORKFLOWS SUBMISSIONS INFO MODAL
  public openWorkflowsSubmissionsInfoSheet(): void {
    this.sheetDialogService$
      .open(new PolymorpheusComponent(WorkflowSubmissionsInfoComponent), {
        closable: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openAllWorkflowsLogsModal() {
    this.dialogService$
      .open(new PolymorpheusComponent(RequestWorkflowEventsDialogComponent), {
        closable: true,
        dismissible: true,
        appearance: 'fullscreen',
        data: {
          allWorkflows: true,
          requestNumber: this.$request().requestNumber,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public openInstallerAssignmentSheet(): void {
    this.sheetDialogService$
      .open<boolean>(
        new PolymorpheusComponent(RequestInstallerAssignmentDialogComponent),
        {
          closable: true,
          data: {
            request: this.$request(),
          },
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updated => {
        if (updated) {
          this.requestService.reloadRequest();
        }
      });
  }
}
